import { useState, useEffect, useRef } from 'react'
import MDEditor, { commands } from '@uiw/react-md-editor'
import { Box, FormHelperText, InputLabel, IconButton, Tooltip, Stack } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import PreviewIcon from '@mui/icons-material/Preview'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen'

export default function MUIMarkdownEditor({
    name,
    label,
    value = '',
    handleChange,
    error = '',
    required = false,
    disabled = false,
    height = 300,
    preview = 'edit',
}) {
  // ✅ local state — decouples editor from parent re-renders
  const [internalValue, setInternalValue] = useState(value)
  const [mode, setMode] = useState(preview)
  const [fullscreen, setFullscreen] = useState(false)

  // ✅ only sync from parent when switching release (value identity changes from outside)
  const prevValueRef = useRef(value)
  useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value
      setInternalValue(value)
    }
  }, [value])

  function onChange(val) {
    const newVal = val ?? ''
    setInternalValue(newVal)
    handleChange(newVal)   // still notifies parent, but parent re-render won't reset cursor
  }

    useEffect(() => {
        setMode(preview)
    } , [preview])

    return (
    <Box
      data-color-mode="light"
      sx={{
        width: '100%',
        // ✅ prevent ANY global float/flex rules leaking in
        '& .w-md-editor *': {
          boxSizing: 'border-box',
          float: 'none',
        },
        '& .w-md-editor-toolbar *': {
          float: 'none !important',
        },
        ...(fullscreen && {
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          backgroundColor: 'background.paper',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }),
      }}
    >
        {/* ── Label Row ── */}
        {/* <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <InputLabel
            required={required}
            error={!!error}
            shrink
            sx={{ position: 'static', transform: 'none', fontSize: 12 }}
            >
            {label}
            </InputLabel>

            {!disabled && (
            <Stack direction="row" spacing={0.5}>
                <Tooltip title="Edit">
                <IconButton size="small" color={mode === 'edit' ? 'primary' : 'default'} onClick={() => setMode('edit')}>
                    <EditIcon fontSize="small" />
                </IconButton>
                </Tooltip>
                <Tooltip title="Preview">
                <IconButton size="small" color={mode === 'preview' ? 'primary' : 'default'} onClick={() => setMode('preview')}>
                    <PreviewIcon fontSize="small" />
                </IconButton>
                </Tooltip>
                <Tooltip title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
                <IconButton size="small" onClick={() => setFullscreen((f) => !f)}>
                    {fullscreen ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                </IconButton>
                </Tooltip>
            </Stack>
            )}
        </Stack> */}

        {/* ── Editor / Preview ── */}
        <Box
            sx={{
                flex: fullscreen ? 1 : 'unset',
                border: (theme) => `1px solid ${error ? theme.palette.error.main : '#000'}`,
                borderRadius: 1,
                overflow: 'auto',
                '& .w-md-editor': { boxShadow: 'none' },
                '& .w-md-editor-toolbar': disabled ? { display: 'none' } : {},

                // ✅ reset global heading/flex styles leaking into CodeMirror
                '& .w-md-editor-text-pre, & .w-md-editor-text-input, & .w-md-editor-text': {
                    fontFamily: 'monospace !important',
                    fontSize: '14px !important',
                },
                '& .w-md-editor-text-pre > code, & .w-md-editor-text-input': {
                    fontFamily: 'monospace !important',
                    lineHeight: '1.6 !important',
                },
                // fix heading tokens floating
                '& .wmde-markdown-color code[class*="language-"]': {
                    display: 'inline !important',
                    float: 'none !important',
                },
                '& .w-md-editor-text-pre .header, & .w-md-editor-text-pre .title': {
                display: 'inline !important',
                    float: 'none !important',
                    textAlign: 'left !important',
                },
            }}
        >
            {disabled ? (
            // read-only: just render markdown preview
            <MDEditor.Markdown
                source={internalValue}
                style={{ 
                    padding: 12, 
                    minHeight: height, 
                    maxHeight: fullscreen ? '100%' : height,
                    color: '#000', 
                    backgroundColor: 'transparent', 
                    whiteSpace: 'pre-wrap' 
                }}
            />
            ) : (
            <MDEditor
                value={internalValue}       // 👈 use local state, not prop
                onChange={onChange}
                preview={'edit'}
                height={fullscreen ? '100%' : height}
                commands={
                    mode === 'edit' ?
                    [
                        commands.bold,
                        commands.italic,
                        commands.strikethrough,
                        commands.hr,
                        commands.divider,
                        commands.title,
                        commands.divider,
                        commands.link,
                        commands.quote,
                        commands.code,
                        commands.codeBlock,
                        commands.divider,
                        commands.unorderedListCommand,
                        commands.orderedListCommand,
                        commands.checkedListCommand,
                    ] : []
                }
                extraCommands={[]}   // hide default fullscreen button — we use our own
            />
            )}
        </Box>

        {error && (
            <FormHelperText error>{error}</FormHelperText>
        )}
        </Box>
    )
}